import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Plus, X, User, Calendar, FileText, GripVertical, Zap, Webhook } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Example data for preview
const EXAMPLE_DATA = {
  'current_user.name': 'Tom Clarke',
  'current_user.email': 'tom@company.com',
  'current_user.first_name': 'Tom',
  'current_date.full': new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
  'current_date.short': new Date().toLocaleDateString('en-GB'),
  'current_date.iso': new Date().toISOString().split('T')[0],
  'current_time': new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  'workflow.name': 'My Workflow',
  'run_number': '#1',
  'flow.files': 'https://example.com/generated-file.pdf',
  'flow.files.0': 'https://example.com/generated-file.pdf',
  'flow.success': 'true',
};

// Available variables that can be inserted
const AVAILABLE_VARIABLES = [
  {
    category: 'User',
    icon: User,
    variables: [
      { key: 'current_user.name', label: 'User Name', description: 'Name of the person running the workflow' },
      { key: 'current_user.email', label: 'User Email', description: 'Email of the person running the workflow' },
      { key: 'current_user.first_name', label: 'First Name', description: 'First name of the person running' },
    ]
  },
  {
    category: 'Date & Time',
    icon: Calendar,
    variables: [
      { key: 'current_date.full', label: 'Full Date', description: 'e.g., December 17, 2025' },
      { key: 'current_date.short', label: 'Short Date', description: 'e.g., 12/17/2025' },
      { key: 'current_date.iso', label: 'ISO Date', description: 'e.g., 2025-12-17' },
      { key: 'current_time', label: 'Current Time', description: 'e.g., 3:30 PM' },
    ]
  },
  {
    category: 'Workflow',
    icon: FileText,
    variables: [
      { key: 'workflow.name', label: 'Workflow Name', description: 'Name of this workflow' },
      { key: 'run_number', label: 'Run Number', description: 'Sequential run number' },
    ]
  },
  {
    category: 'Flow Output',
    icon: Zap,
    variables: [
      { key: 'flow.files', label: 'Flow Files', description: 'First file URL from flow output' },
      { key: 'flow.files.0', label: 'First File', description: 'First file URL (explicit index)' },
      { key: 'flow.success', label: 'Flow Success', description: 'Whether the flow succeeded' },
    ]
  },
];

// Parse template string into segments
const parseTemplate = (template) => {
  if (!template) return [];

  const segments = [];
  const regex = /\{\{\s*([^}]+)\s*\}\}/g;
  let lastIndex = 0;
  let match;
  let segmentId = 0;

  while ((match = regex.exec(template)) !== null) {
    // Add text before this variable (if any)
    if (match.index > lastIndex) {
      const text = template.slice(lastIndex, match.index);
      if (text.trim()) {
        segments.push({
          id: `seg-${segmentId++}`,
          type: 'text',
          value: text.trim(),
        });
      }
    }

    // Add the variable
    segments.push({
      id: `seg-${segmentId++}`,
      type: 'variable',
      value: match[1].trim(),
    });

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text
  if (lastIndex < template.length) {
    const text = template.slice(lastIndex);
    if (text.trim()) {
      segments.push({
        id: `seg-${segmentId++}`,
        type: 'text',
        value: text.trim(),
      });
    }
  }

  return segments;
};

// Convert segments back to template string
const segmentsToTemplate = (segments) => {
  return segments.map(seg => {
    if (seg.type === 'variable') {
      return `{{ ${seg.value} }}`;
    }
    return seg.value;
  }).join(' ');
};

// Find variable info by key - checks built-in variables, webhook vars, and optional form field map
let _formFieldMap = {};
const setFormFieldMap = (map) => { _formFieldMap = map || {}; };
let _webhookVariables = [];
const setWebhookVariablesMap = (vars) => { _webhookVariables = vars || []; };

const findVariableInfo = (key) => {
  for (const category of AVAILABLE_VARIABLES) {
    const variable = category.variables.find(v => v.key === key);
    if (variable) {
      return { ...variable, category: category.category, icon: category.icon };
    }
  }
  // Check webhook variables
  if (key.startsWith('webhook.')) {
    const webhookVar = _webhookVariables.find(v => v.key === key);
    if (webhookVar) {
      return { key, label: key.replace('webhook.', ''), description: `Sample: ${webhookVar.sampleValue}`, category: 'Webhook', icon: Webhook };
    }
    return { key, label: key.replace('webhook.', ''), description: 'Webhook variable', category: 'Webhook', icon: Webhook };
  }
  // Check form field map for resolving raw IDs to friendly names
  if (_formFieldMap[key]) {
    return { key, label: _formFieldMap[key], description: 'Form field', category: 'Form', icon: FileText };
  }
  return { key, label: key, description: 'Custom variable' };
};

// Get icon for variable
const getVariableIcon = (key) => {
  if (key.startsWith('current_user')) return User;
  if (key.startsWith('current_date') || key.startsWith('current_time')) return Calendar;
  if (key.startsWith('flow.')) return Zap;
  if (key.startsWith('webhook.')) return Webhook;
  if (key.startsWith('workflow') || key.startsWith('run_')) return FileText;
  return FileText;
};

// Generate unique ID
let idCounter = 0;
const generateId = () => `seg-${Date.now()}-${idCounter++}`;

// Generate preview with real data
const generatePreview = (template, overrides = {}, webhookVars = []) => {
  if (!template) return '';
  // Build webhook example data from saved variables
  const webhookData = {};
  if (webhookVars) {
    webhookVars.forEach(v => { webhookData[v.key] = v.sampleValue; });
  }
  const data = { ...EXAMPLE_DATA, ...webhookData, ...overrides };

  return template.replace(/\{\{\s*([^}]+)\s*\}\}/g, (match, key) => {
    const trimmedKey = key.trim();
    return data[trimmedKey] || `[${trimmedKey}]`;
  });
};

// Sortable Variable Pill
function SortableVariablePill({ segment, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: segment.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const varInfo = findVariableInfo(segment.value);
  const IconComponent = getVariableIcon(segment.value);

  return (
    <Badge
      ref={setNodeRef}
      style={style}
      variant="secondary"
      className="gap-1 pl-1.5 pr-1 py-1 h-7 bg-primary/10 text-primary hover:bg-primary/20 cursor-grab active:cursor-grabbing select-none"
    >
      <span {...attributes} {...listeners} className="cursor-grab">
        <GripVertical className="w-3 h-3 text-primary/50" />
      </span>
      <IconComponent className="w-3 h-3" />
      <span className="text-xs font-medium">{varInfo.label}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="ml-0.5 hover:bg-primary/30 rounded p-0.5"
      >
        <X className="w-3 h-3" />
      </button>
    </Badge>
  );
}

// Sortable Text Chip with inline editing
function SortableTextChip({ segment, onRemove, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(segment.value);
  const inputRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: segment.id, disabled: isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed) {
      onUpdate(trimmed);
    } else {
      onRemove();
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(segment.value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="h-7 px-2 text-xs font-medium bg-muted border border-primary rounded-md outline-none min-w-[40px] max-w-[120px]"
        style={{ width: `${Math.max(40, editValue.length * 8)}px` }}
      />
    );
  }

  return (
    <Badge
      ref={setNodeRef}
      style={style}
      variant="outline"
      className="gap-1 pl-1.5 pr-1 py-1 h-7 bg-muted/50 hover:bg-muted cursor-grab active:cursor-grabbing select-none"
      onDoubleClick={() => setIsEditing(true)}
    >
      <span {...attributes} {...listeners} className="cursor-grab">
        <GripVertical className="w-3 h-3 text-muted-foreground/50" />
      </span>
      <span
        className="text-xs font-medium text-muted-foreground cursor-text"
        onClick={() => setIsEditing(true)}
      >
        {segment.value}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="ml-0.5 hover:bg-muted-foreground/20 rounded p-0.5"
      >
        <X className="w-3 h-3 text-muted-foreground" />
      </button>
    </Badge>
  );
}

export function VariablePillEditor({ value, onChange, placeholder = 'Add variables and text...', exampleData, formFieldMap, webhookVariables }) {
  // Update the module-level form field map so findVariableInfo can resolve form field IDs
  useEffect(() => {
    if (formFieldMap) setFormFieldMap(formFieldMap);
    return () => setFormFieldMap({});
  }, [formFieldMap]);

  // Update module-level webhook variables for findVariableInfo
  useEffect(() => {
    if (webhookVariables) setWebhookVariablesMap(webhookVariables);
    return () => setWebhookVariablesMap([]);
  }, [webhookVariables]);
  const [segments, setSegments] = useState(() => parseTemplate(value || ''));
  const [showVariables, setShowVariables] = useState(false);
  const [isAddingText, setIsAddingText] = useState(false);
  const [newText, setNewText] = useState('');
  const textInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync with external value changes
  useEffect(() => {
    const parsed = parseTemplate(value || '');
    setSegments(parsed);
  }, [value]);

  // Focus text input when adding
  useEffect(() => {
    if (isAddingText && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [isAddingText]);

  const updateValue = (newSegments) => {
    setSegments(newSegments);
    onChange(segmentsToTemplate(newSegments));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = segments.findIndex(s => s.id === active.id);
      const newIndex = segments.findIndex(s => s.id === over.id);
      updateValue(arrayMove(segments, oldIndex, newIndex));
    }
  };

  const handleAddVariable = (variableKey) => {
    const newSegments = [...segments, { id: generateId(), type: 'variable', value: variableKey }];
    updateValue(newSegments);
    setShowVariables(false);
  };

  const handleAddText = () => {
    const trimmed = newText.trim();
    if (trimmed) {
      const newSegments = [...segments, { id: generateId(), type: 'text', value: trimmed }];
      updateValue(newSegments);
    }
    setNewText('');
    setIsAddingText(false);
  };

  const handleTextKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddText();
    } else if (e.key === 'Escape') {
      setNewText('');
      setIsAddingText(false);
    }
  };

  const handleRemoveSegment = (id) => {
    updateValue(segments.filter(s => s.id !== id));
  };

  const handleUpdateSegment = (id, newValue) => {
    updateValue(segments.map(s => s.id === id ? { ...s, value: newValue } : s));
  };

  return (
    <div className="space-y-2">
      {/* Display area */}
      <div className="min-h-[44px] p-2 border rounded-md bg-background flex flex-wrap items-center gap-1.5">
        {segments.length === 0 && !isAddingText && (
          <span className="text-muted-foreground text-sm">{placeholder}</span>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={segments.map(s => s.id)}
            strategy={horizontalListSortingStrategy}
          >
            {segments.map((segment) => (
              segment.type === 'variable' ? (
                <SortableVariablePill
                  key={segment.id}
                  segment={segment}
                  onRemove={() => handleRemoveSegment(segment.id)}
                />
              ) : (
                <SortableTextChip
                  key={segment.id}
                  segment={segment}
                  onRemove={() => handleRemoveSegment(segment.id)}
                  onUpdate={(newValue) => handleUpdateSegment(segment.id, newValue)}
                />
              )
            ))}
          </SortableContext>
        </DndContext>

        {/* Inline text input */}
        {isAddingText && (
          <input
            ref={textInputRef}
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onBlur={handleAddText}
            onKeyDown={handleTextKeyDown}
            placeholder="Type text..."
            className="h-7 px-2 text-xs bg-transparent border-b border-primary outline-none min-w-[80px]"
          />
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsAddingText(true)}
          disabled={isAddingText}
          className="h-7 text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          Text
        </Button>

        <Popover open={showVariables} onOpenChange={setShowVariables}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Variable
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <div className="p-2 border-b">
              <p className="text-sm font-medium">Insert Variable</p>
              <p className="text-xs text-muted-foreground">Click to add to template</p>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {(() => {
                // Build categories list dynamically, adding Webhook if variables exist
                const categories = [...AVAILABLE_VARIABLES];
                if (webhookVariables && webhookVariables.length > 0) {
                  categories.push({
                    category: 'Webhook',
                    icon: Webhook,
                    variables: webhookVariables.map(v => ({
                      key: v.key,
                      label: v.key.replace('webhook.', ''),
                      description: `Sample: ${v.sampleValue}`,
                    })),
                  });
                }
                return categories.map((category) => (
                  <div key={category.category}>
                    <div className="px-3 py-1.5 bg-muted/50 flex items-center gap-2">
                      <category.icon className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">{category.category}</span>
                    </div>
                    {category.variables.map((variable) => (
                      <button
                        key={variable.key}
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-muted transition-colors"
                        onClick={() => handleAddVariable(variable.key)}
                      >
                        <div className="text-sm font-medium">{variable.label}</div>
                        <div className="text-xs text-muted-foreground">{variable.description}</div>
                      </button>
                    ))}
                  </div>
                ));
              })()}
            </div>
          </PopoverContent>
        </Popover>

        {segments.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => updateValue([])}
            className="h-7 text-xs text-muted-foreground hover:text-destructive ml-auto"
          >
            Clear
          </Button>
        )}
      </div>

      {/* Preview */}
      {segments.length > 0 && (
        <div className="px-3 py-2 bg-muted/30 rounded-md border border-dashed">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Preview</div>
          <div className="text-sm font-medium">{generatePreview(segmentsToTemplate(segments), exampleData, webhookVariables)}</div>
        </div>
      )}
    </div>
  );
}
